import React from "react";
import "./css/App.css";

import {
  Grid,
  Grow,
  useMediaQuery,
  useTheme,
  Card,
  SwipeableDrawer,
  styled,
  Box,
} from "@mui/material";
import NuclideGraph from "components/NuclideGraph";
import nuclides, { getNuclide } from "core/services/nuclideService";
import PeriodicTable from "components/PeriodicTable";
import NuclideUsageDetail from "components/NuclideUsageDetail";
import NuclideControlCard from "components/NuclideControlCard";
import { grey } from "@mui/material/colors";
import debounce from "lodash/debounce";
import HeaderBar from "HeaderBar";

const graphDefaultSizeSetting = {
  desktop: {
    width: {
      ratio: 0.48,
    },
    height: {
      ratio: 0.35,
      max: 400,
    },
  },
  mobile: {
    width: {
      ratio: 0.48,
    },
    height: {
      ratio: 0.25,
      max: 400,
    },
  },
};

const fullSCreenSetting = {
  width: {
    ratio: 0.9,
  },
  height: {
    ratio: 0.8,
  },
};

const debounceSetURL = debounce((newNuclide) => {
  if (newNuclide?.symbol)
    window.history.replaceState(null, "", `?search=${newNuclide.symbol}`);
}, 400);

const Puller = styled(Box)(() => ({
  width: 60,
  height: 6,
  backgroundColor: grey[300],
  borderRadius: 3,
  position: "absolute",
  top: 8,
  left: "calc(50% - 30px)",
}));

const EMPTY_FUNC = () => {};

const SWIPE_PAPER_PROPS = {
  sx: {
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
  },
};

const Dashboard = () => {
  const [selectedNuclide, defaultSetSelectedNuclide] = React.useState(() => {
    const search = new URLSearchParams(window.location.search).get("search");
    if (search) {
      const loweredSearch = search.toLowerCase().trim();
      const target = nuclides.flatten.find(
        (n) =>
          n.symbol.toLowerCase().trim() === loweredSearch ||
          n.name.toLowerCase().trim() === loweredSearch
      );
      if (target) {
        return target;
      }
    }
    return getNuclide({ proton: 1, neutron: 1 });
  });

  const setSelectedNuclide = React.useCallback(
    (newNuclide) => {
      defaultSetSelectedNuclide((on) => {
        if (typeof newNuclide === "function") {
          newNuclide = newNuclide(on);
        }
        debounceSetURL(newNuclide);

        return newNuclide;
      });
    },
    [defaultSetSelectedNuclide]
  );
  const [fullScreenGraph, setFullScreenGraph] = React.useState(null);
  const [openDrawer, setOpenDrawer] = React.useState(null);
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  const isNuclideExist = Boolean(selectedNuclide?.name);
  const isPeriodicTableFullScreen = fullScreenGraph === "PeriodicTable";
  const isNuclideGraphFullScreen = fullScreenGraph === "NuclideGraph";

  const handleClose = React.useCallback(() => {
    setOpenDrawer(false);
  }, []);

  return (
    <>
      <HeaderBar setSelectedNuclide={setSelectedNuclide} />
      <Grid container>
        {(!fullScreenGraph || isPeriodicTableFullScreen) && (
          <Grid item xs={isPeriodicTableFullScreen ? 12 : 6}>
            <Grow in>
              <div>
                <PeriodicTable
                  selectedNuclide={selectedNuclide}
                  defaultSizeSetting={
                    isPeriodicTableFullScreen
                      ? fullSCreenSetting
                      : isDesktop
                      ? graphDefaultSizeSetting.desktop
                      : graphDefaultSizeSetting.mobile
                  }
                  setSelectedNuclide={setSelectedNuclide}
                  isFullScreen={isPeriodicTableFullScreen}
                  setFullScreenGraph={setFullScreenGraph}
                />
              </div>
            </Grow>
          </Grid>
        )}

        {(!fullScreenGraph || isNuclideGraphFullScreen) && (
          <Grid item xs={isNuclideGraphFullScreen ? 12 : 6}>
            <Grow in>
              <div>
                <NuclideGraph
                  selectedNuclide={selectedNuclide}
                  setSelectedNuclide={setSelectedNuclide}
                  setFullScreenGraph={setFullScreenGraph}
                  isFullScreen={isNuclideGraphFullScreen}
                  defaultSizeSetting={
                    isNuclideGraphFullScreen
                      ? fullSCreenSetting
                      : isDesktop
                      ? graphDefaultSizeSetting.desktop
                      : graphDefaultSizeSetting.mobile
                  }
                />
              </div>
            </Grow>
          </Grid>
        )}

        {!fullScreenGraph && (
          <>
            {isDesktop ? (
              <Grid item xs={6}>
                {isNuclideExist && (
                  <Grow in>
                    <Card square>
                      <NuclideUsageDetail selectedNuclide={selectedNuclide} />
                    </Card>
                  </Grow>
                )}
              </Grid>
            ) : (
              <SwipeableDrawer
                onOpen={EMPTY_FUNC}
                anchor="bottom"
                disableDiscovery
                PaperProps={SWIPE_PAPER_PROPS}
                open={Boolean(openDrawer)}
                disableSwipeToOpen
                onClose={handleClose}
              >
                <Puller />
                <br />
                <NuclideUsageDetail selectedNuclide={selectedNuclide} />
              </SwipeableDrawer>
            )}
            <Grid item xs={12} md={6}>
              <Grow in>
                <Box height="100%">
                  <NuclideControlCard
                    isDesktop={isDesktop}
                    setOpenDrawer={setOpenDrawer}
                    selectedNuclide={selectedNuclide}
                    setSelectedNuclide={setSelectedNuclide}
                  />
                </Box>
              </Grow>
            </Grid>
          </>
        )}
      </Grid>
    </>
  );
};

export default Dashboard;
